import type { ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import { deriveSelectableToolHandleSet } from '@/workflow-builder/_pure/model/toolSet';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { ActionContext } from '@/workflow-builder/_contract/context';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';

function isLoopWhatSlotSelection(args: {
    runtimeState: RuntimeState;
}): boolean {
    const { runtimeState } = args;
    if (runtimeState.phase !== 'selecting_tool_at_slot') {
        return false;
    }

    if (runtimeState.activeSlotCursor?.cursorKind !== 'case/what') {
        return false;
    }

    return runtimeState.draft?.draftKind === 'for' || runtimeState.draft?.draftKind === 'while';
}

export function deriveContextualSelectableToolHandleSet(args: {
    config: Config;
    cosmosData: CosmosDataModel;
    runtimeState: RuntimeState;
}): ReadonlySet<ToolHandle> {
    const baseSelectableToolHandleSet = deriveSelectableToolHandleSet({
        config: args.config,
        cosmosData: args.cosmosData,
    });

    if (!isLoopWhatSlotSelection({ runtimeState: args.runtimeState })) {
        return baseSelectableToolHandleSet;
    }

    const loopWhatSlotEligibleToolHandleSet = new Set(
        args.cosmosData.loopStepWhatSlotEligibleToolHandles.map((toolHandle) => String(toolHandle) as ToolHandle),
    );

    return new Set(
        Array.from(baseSelectableToolHandleSet).filter((toolHandle) => loopWhatSlotEligibleToolHandleSet.has(toolHandle)),
    );
}

export function createActionContext(args: {
    config: Config;
    cosmosData: CosmosDataModel;
    runtimeState: RuntimeState;
}): ActionContext {
    const selectableToolHandleSet = deriveContextualSelectableToolHandleSet({
        config: args.config,
        cosmosData: args.cosmosData,
        runtimeState: args.runtimeState,
    });

    const baseSelectableToolHandleSet = deriveSelectableToolHandleSet({
        config: args.config,
        cosmosData: args.cosmosData,
    });

    return {
        selectableToolHandleSet,
        ineligibleSelectableToolHandleSet: new Set(
            Array.from(baseSelectableToolHandleSet).filter((toolHandle) => !selectableToolHandleSet.has(toolHandle)),
        ),
        isLoopWhatSlotSelection: isLoopWhatSlotSelection({ runtimeState: args.runtimeState }),
    };
}
